import axios from 'axios';
import { ApiService } from '@shared/services/api.service';
import { apiErrorStatus } from './auditorium-api-error';
import {
  AuditoriumAttendee,
  AuditoriumAttendeeSearchResult,
  AuditoriumRole,
  AuditoriumRoleAssignment,
} from '../types';

/**
 * Moderator-panel API calls for the auditorium (NGE-1078): attendee search and
 * promote/demote. Kept out of the components so the search hook, the role actions
 * and the role-sync poller share one place that knows the endpoint shapes.
 */

/** Shared abort id: ApiService cancels the in-flight search when a new query starts. */
const ATTENDEE_SEARCH_ABORT_ID = 'auditorium-attendee-search';

/** Matches the backend default page size for the attendee search. */
export const ATTENDEE_SEARCH_LIMIT = 20;

/** True for the cancellation ApiService raises when a newer search supersedes this one. */
export const isAbortError = (error: unknown): boolean => axios.isCancel(error);

/** True when the caller has lost moderator rights (e.g. demoted while the panel was open). */
export const isForbiddenError = (error: unknown): boolean => apiErrorStatus(error) === 403;

/**
 * Attendees of the auditorium filtered by name/email. An empty query returns the
 * first page, so the panel can show a roster before anything is typed. Members and
 * guests both come back — guests are promotable (NGE-1078), so nothing is filtered
 * out client-side.
 */
export const searchAuditoriumAttendees = (
  sessionPid: string,
  query: string
): Promise<AuditoriumAttendeeSearchResult> =>
  ApiService.request<AuditoriumAttendeeSearchResult>({
    endpoint: `auditorium/${sessionPid}/attendees`,
    method: 'GET',
    data: { q: query || undefined, limit: ATTENDEE_SEARCH_LIMIT },
    abortId: ATTENDEE_SEARCH_ABORT_ID,
  });

/**
 * Promote or demote one attendee. The target is addressed by `user_pid` for a member
 * and `guest_pid` for a guest — the backend requires exactly one of the two.
 */
export const assignAuditoriumRole = (
  sessionPid: string,
  attendee: Pick<AuditoriumAttendee, 'pid' | 'type'>,
  role: AuditoriumRole
): Promise<AuditoriumRoleAssignment> =>
  ApiService.request<AuditoriumRoleAssignment>({
    endpoint: `auditorium/${sessionPid}/roles`,
    method: 'POST',
    data: {
      ...(attendee.type === 'guest' ? { guest_pid: attendee.pid } : { user_pid: attendee.pid }),
      role,
    },
  });

/** The caller's own effective role — cheap enough to poll (see auditorium-role-sync). */
export const fetchMyAuditoriumRole = (
  sessionPid: string
): Promise<{
  session_pid: string;
  role: AuditoriumRole;
  can_moderate?: boolean;
  can_manage_room?: boolean;
}> =>
  ApiService.request<{
    session_pid: string;
    role: AuditoriumRole;
    can_moderate?: boolean;
    can_manage_room?: boolean;
  }>({
    endpoint: `auditorium/${sessionPid}/me/role`,
    method: 'GET',
  });
